/**
 * Phone normalisation for rent reminders.
 *
 * WhatsApp's wa.me links and sms: links both need a full international number
 * without the leading 0 Nigerians usually write. Tenants' numbers are typed in
 * by hand, so we accept the common shapes:
 *   08031234567, 0803 123 4567, 8031234567,
 *   +2348031234567, 2348031234567, 002348031234567, +234 (0) 803 123 4567
 * and reject anything that is not a Nigerian mobile number (10 national digits
 * starting with 7, 8 or 9), e.g. landlines, which cannot receive WhatsApp.
 *
 * A number written with an explicit non-Nigerian country code ("+44 ...") is
 * accepted as-is, because some tenants live abroad.
 */

export type PhoneResult =
  | { ok: true; digits: string; display: string }
  | { ok: false; reason: string };

const NIGERIA_CC = '234';
const NG_MOBILE = /^[789]\d{9}$/;
const E164_DIGITS = /^[1-9]\d{7,14}$/;

function invalid(raw: string): PhoneResult {
  return {
    ok: false,
    reason: `Phone number "${raw}" is not a valid Nigerian mobile number`,
  };
}

function nigerian(national: string): PhoneResult {
  const digits = `${NIGERIA_CC}${national}`;
  const display = `+${NIGERIA_CC} ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
  return { ok: true, digits, display };
}

export function normaliseNigerianPhone(
  raw: string | null | undefined,
): PhoneResult {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return { ok: false, reason: 'No phone number on file' };

  const hasPlus = trimmed.startsWith('+');
  // "+234 (0) 803..." — the (0) is a trunk prefix that must be dropped.
  const withoutTrunk = trimmed.replace(/\(0\)/g, '');
  if (/[^\d\s()+.-]/.test(withoutTrunk)) return invalid(trimmed);

  let digits = withoutTrunk.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);

  const internationalNg =
    hasPlus ||
    trimmed.startsWith('00') ||
    (digits.startsWith(NIGERIA_CC) && digits.length >= 13);
  if (internationalNg && digits.startsWith(NIGERIA_CC)) {
    let national = digits.slice(NIGERIA_CC.length);
    // Common mistake: "+2340803..." keeps the local 0 after the country code.
    if (national.length === 11 && national.startsWith('0'))
      national = national.slice(1);
    return NG_MOBILE.test(national) ? nigerian(national) : invalid(trimmed);
  }

  if (hasPlus || trimmed.startsWith('00')) {
    // Explicit foreign country code.
    if (!E164_DIGITS.test(digits)) return invalid(trimmed);
    return { ok: true, digits, display: `+${digits}` };
  }

  if (digits.length === 11 && digits.startsWith('0')) {
    const national = digits.slice(1);
    return NG_MOBILE.test(national) ? nigerian(national) : invalid(trimmed);
  }
  if (NG_MOBILE.test(digits)) return nigerian(digits);
  return invalid(trimmed);
}
